import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Layer, PropertyRow } from './Layer';
import { FeatureItem, type FeatureItemProps } from './FeatureItem';
import { useUscgMapContext } from './UscgMapContext';
import { useListInternalLayerVersionFeaturesQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { InternalLayer } from 'hooks/internalLayer/types';
import { ExternalLayer } from 'hooks/externalLayer/types';
import { ExternalLayerFeature } from './utils/queryExternalLayerFeatures';
import { getExternalFeatureTitle } from './utils/getExternalFeatureTitle';
import { InternalFeatureList } from './InternalFeatureList';
import {
  attributeStoredFields,
  locationStoredFields,
} from './utils/locationPopup';
import { buildStoredFieldsGraphic } from './utils/focusFeatureOnMap';

type LayerFeaturesContentProps = {
  layer: InternalLayer | ExternalLayer;
  layerType: 'internal' | 'external';
  showAddObjectButton?: boolean;
  showDeleteObjectButton?: boolean;
  onZoomToExternalGraphic?: (graphic: __esri.Graphic) => void;
  onEdit?: (featureId: string) => Promise<void> | void;
  onDelete?: (featureId: string) => Promise<void> | void;
  /**
   * Override FeatureItem chrome (e.g. SitStat shadcn). Defaults to LayersTab
   * FeatureItem.
   */
  FeatureItemComponent?: ComponentType<FeatureItemProps>;
};

export function LayerFeaturesContent({
  layer,
  layerType,
  onZoomToExternalGraphic,
  onEdit,
  onDelete,
  showAddObjectButton,
  FeatureItemComponent = FeatureItem,
}: LayerFeaturesContentProps) {
  const {
    isPublicExternalLayersReady,
    isProtectedExternalLayersReady,
    isExternalLayersReady,
    getExternalLayerFeatures,
    focusFeatureOnMap,
    focusLayerFeatureOnMap,
    getSelectedVersionForLayer,
  } = useUscgMapContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);

  const selectedVersion =
    layerType === 'internal' ? getSelectedVersionForLayer(layer.id) : undefined;
  const { data: internalFeatures = [], isLoading: internalLoading } =
    useListInternalLayerVersionFeaturesQuery(selectedVersion?.id);

  const [externalFeatures, setExternalFeatures] = useState<
    ExternalLayerFeature[]
  >([]);
  const [externalLoading, setExternalLoading] = useState(false);

  const fetchExternal = useCallback(async () => {
    if (layerType !== 'external') return;

    setExternalLoading(true);

    try {
      const result = await getExternalLayerFeatures(layer.id);
      setExternalFeatures(result);
    } catch (error) {
      console.error('error fetching external features for ', layer.id, error);
      setExternalFeatures([]);
    } finally {
      setExternalLoading(false);
    }
  }, [layer.id, layerType, getExternalLayerFeatures]);

  useEffect(() => {
    if (layerType !== 'external') {
      return;
    }

    const authType = layer.authType;

    if (
      (authType === 'apiKey' || authType === 'public') &&
      isPublicExternalLayersReady
    ) {
      fetchExternal();
      return;
    }

    if (authType === 'userAuth' && isProtectedExternalLayersReady) {
      fetchExternal();
      return;
    }

    if (authType === null && isExternalLayersReady) {
      fetchExternal();
    }
  }, [
    fetchExternal,
    isExternalLayersReady,
    isProtectedExternalLayersReady,
    isPublicExternalLayersReady,
    layer.id,
    layer.authType,
    layerType,
  ]);

  const editingLocation =
    layerType === 'internal'
      ? (internalFeatures.find((feature) => feature.id === editingFeatureId) ??
        null)
      : null;
  const canEditObjects =
    layerType === 'internal' &&
    selectedVersion?.approval?.approvalStatus === 'DRAFT';

  const isLoading =
    layerType === 'internal' ? internalLoading : externalLoading;

  if (isLoading) {
    return <Layer.FeaturesList isLoading />;
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredInternalFeatures = normalizedSearch
    ? internalFeatures.filter((f) =>
        (f.name ?? '').toLowerCase().includes(normalizedSearch)
      )
    : internalFeatures;

  return (
    <Layer.FeaturesList
      internalLayer={layerType === 'internal' ? layer : null}
      showAddObjectButton={
        Boolean(showAddObjectButton) &&
        (selectedVersion?.approval?.approvalStatus === 'DRAFT' ||
          Boolean(
            layerType === 'internal' &&
              ((layer as InternalLayer).viewerCanEdit ||
                (layer as InternalLayer).publishedAt != null)
          ))
      }
      searchTerm={searchTerm}
      onChangeSearchTerm={setSearchTerm}
      showSearch={layerType === 'internal'}
      editingLocation={editingLocation}
      onCancelEdit={() => setEditingFeatureId(null)}
    >
      {layerType === 'internal' ? (
        <InternalFeatureList
          editingFeatureId={editingFeatureId}
          onEdit={
            canEditObjects
              ? async (featureId) => {
                  setEditingFeatureId(featureId);
                  await onEdit?.(featureId);
                }
              : onEdit
          }
          onDelete={onDelete}
          FeatureItemComponent={FeatureItemComponent}
          onFocusFeature={(feature) => {
            const source = internalFeatures.find(
              (item) => item.id === feature.featureId
            );
            const graphic = buildStoredFieldsGraphic({
              geometry: feature.geometry,
              title: feature.title,
              fields: source ? locationStoredFields(source) : [],
            });
            if (graphic) {
              void focusFeatureOnMap({
                graphic,
                layer: { kind: 'internal', layerId: layer.id },
              });
              return;
            }
            void focusLayerFeatureOnMap({
              kind: 'internal',
              layerId: layer.id,
              featureId: feature.featureId,
              geometry: feature.geometry,
              attributes: {
                id: feature.featureId,
                name: feature.title,
                resourceType: feature.resourceType,
              },
              title: feature.title,
            });
          }}
          features={filteredInternalFeatures.map((f) => ({
            featureId: f.id,
            key: f.id,
            title: f.name,
            resourceType: f.resourceType,
            properties: f.properties,
            updatedAt: formatDateTime(f.updatedAt),
            geometry: f.geometry,
            fields: locationStoredFields(f),
          }))}
        />
      ) : (
        <ExternalFeatureList
          FeatureItemComponent={FeatureItemComponent}
          features={externalFeatures}
          onZoom={(graphic) => {
            if (onZoomToExternalGraphic) {
              onZoomToExternalGraphic(graphic);
              return;
            }
            void focusFeatureOnMap({
              graphic,
              layer: { kind: 'external', layerId: layer.id },
            });
          }}
        />
      )}
    </Layer.FeaturesList>
  );
}

function ExternalAttributeRows({
  attributes,
}: {
  attributes: Record<string, unknown> | null | undefined;
}) {
  const fields = attributeStoredFields(attributes);
  if (fields.length === 0) return null;
  return (
    <>
      {fields.map((field, index) => (
        <PropertyRow
          key={`${field.label}-${index}`}
          label={field.label}
          value={field.value}
        />
      ))}
    </>
  );
}

function ExternalFeatureList({
  features,
  onZoom,
  FeatureItemComponent = FeatureItem,
}: {
  features: ExternalLayerFeature[];
  onZoom?: (graphic: __esri.Graphic) => void;
  FeatureItemComponent?: ComponentType<FeatureItemProps>;
}) {
  const Item = FeatureItemComponent;

  return (
    <ul
      className="list-unstyled d-flex flex-column mb-0 gap-1"
      style={{ maxHeight: '200px', overflowY: 'auto' }}
    >
      {features.map((feature, index) => {
        const id = feature.attributes.OBJECTID ?? index;

        return (
          <Item
            key={id}
            externalFeature={{
              featureId: id,
              symbol: feature.symbol,
            }}
            title={getExternalFeatureTitle(
              feature.attributes,
              feature.displayField
            )}
            onZoom={() => onZoom?.(feature)}
          >
            <ExternalAttributeRows
              attributes={feature.attributes as Record<string, unknown>}
            />
          </Item>
        );
      })}
    </ul>
  );
}
